import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { WhatsAppState, WhatsAppChat, WhatsAppMessage, ChatFilters, SendMessageRequest } from '../../features/whatsapp/types';

// État initial
const initialState: WhatsAppState = {
  chats: [],
  selectedChatId: null,
  messages: {},
  isLoading: false,
  error: null,
  filters: {},
  metrics: null,
  agentPerformances: [],
  ui: {
    sidebarOpen: true,
    chatWindowHeight: 600,
    typingIndicators: {},
    messageInput: {},
  },
};

// Thunks asynchrones
export const fetchChats = createAsyncThunk(
  'whatsapp/fetchChats',
  async (filters: ChatFilters = {}) => {
    const response = await fetch('/api/v1/whatsapp/chats?' + new URLSearchParams(filters as any));
    if (!response.ok) throw new Error('Failed to fetch chats');
    return response.json();
  }
);

export const fetchMessages = createAsyncThunk(
  'whatsapp/fetchMessages',
  async ({ chatId, limit = 50, offset = 0 }: { chatId: string; limit?: number; offset?: number }) => {
    const response = await fetch(`/api/v1/whatsapp/chats/${chatId}/messages?limit=${limit}&offset=${offset}`);
    if (!response.ok) throw new Error('Failed to fetch messages');
    return response.json();
  }
);

export const sendMessage = createAsyncThunk(
  'whatsapp/sendMessage',
  async (request: SendMessageRequest) => {
    const response = await fetch('/api/v1/whatsapp/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) throw new Error('Failed to send message');
    return response.json();
  }
);

// Slice
export const whatsappSlice = createSlice({
  name: 'whatsapp',
  initialState,
  reducers: {
    // Sélection de chat
    selectChat: (state, action: PayloadAction<string | null>) => {
      state.selectedChatId = action.payload;
    },

    // Gestion des filtres
    setWhatsAppFilters: (state, action: PayloadAction<ChatFilters>) => {
      state.filters = action.payload;
    },

    clearWhatsAppFilters: (state) => {
      state.filters = {};
    },

    // Gestion de l'UI
    toggleWhatsAppSidebar: (state) => {
      state.ui.sidebarOpen = !state.ui.sidebarOpen;
    },

    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.sidebarOpen = action.payload;
    },

    setChatWindowHeight: (state, action: PayloadAction<number>) => {
      state.ui.chatWindowHeight = action.payload;
    },

    // Indicateurs de frappe
    setTypingIndicator: (state, action: PayloadAction<{ chatId: string; isTyping: boolean }>) => {
      const { chatId, isTyping } = action.payload;
      state.ui.typingIndicators[chatId] = isTyping;
    },

    // Brouillons de messages
    setMessageInput: (state, action: PayloadAction<{ chatId: string; content: string }>) => {
      const { chatId, content } = action.payload;
      state.ui.messageInput[chatId] = content;
    },

    clearMessageInput: (state, action: PayloadAction<string>) => {
      delete state.ui.messageInput[action.payload];
    },

    // Mise à jour optimiste des messages
    addOptimisticMessage: (state, action: PayloadAction<WhatsAppMessage>) => {
      const message = action.payload;
      if (!state.messages[message.chatId]) {
        state.messages[message.chatId] = [];
      }
      state.messages[message.chatId].push(message);
    },

    updateMessageStatus: (state, action: PayloadAction<{ messageId: string; status: WhatsAppMessage['status'] }>) => {
      const { messageId, status } = action.payload;
      
      // Rechercher et mettre à jour le message dans tous les chats
      Object.keys(state.messages).forEach(chatId => {
        const messageIndex = state.messages[chatId].findIndex(m => m.id === messageId);
        if (messageIndex !== -1) {
          state.messages[chatId][messageIndex].status = status;
        }
      });
    },

    // Réception de nouveau message en temps réel
    receiveMessage: (state, action: PayloadAction<WhatsAppMessage>) => {
      const message = action.payload;
      if (!state.messages[message.chatId]) {
        state.messages[message.chatId] = [];
      }
      
      // Vérifier si le message n'existe pas déjà
      const exists = state.messages[message.chatId].some(m => m.id === message.id);
      if (!exists) {
        state.messages[message.chatId].push(message);
      }

      // Mettre à jour le chat correspondant
      const chatIndex = state.chats.findIndex(c => c.id === message.chatId);
      if (chatIndex !== -1) {
        state.chats[chatIndex].lastMessage = message;
        state.chats[chatIndex].updatedAt = message.timestamp;
        if (message.sender === 'user') {
          state.chats[chatIndex].unreadCount += 1;
        }
      }
    },

    // Marquer messages comme lus
    markMessagesAsRead: (state, action: PayloadAction<{ chatId: string; messageIds: string[] }>) => {
      const { chatId, messageIds } = action.payload;
      
      if (state.messages[chatId]) {
        state.messages[chatId].forEach(message => {
          if (messageIds.includes(message.id) && message.sender === 'user') {
            message.status = 'read';
          }
        });
      }

      // Mettre à jour le compteur de messages non lus
      const chatIndex = state.chats.findIndex(c => c.id === chatId);
      if (chatIndex !== -1) {
        state.chats[chatIndex].unreadCount = Math.max(0, state.chats[chatIndex].unreadCount - messageIds.length);
      }
    },

    // Erreurs
    setWhatsAppError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    clearWhatsAppError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    // Fetch chats
    builder
      .addCase(fetchChats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchChats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.chats = action.payload.chats || action.payload;
      })
      .addCase(fetchChats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch chats';
      });

    // Fetch messages
    builder
      .addCase(fetchMessages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.isLoading = false;
        const { chatId, messages } = action.payload;
        if (chatId && messages) {
          state.messages[chatId] = messages;
        }
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch messages';
      });

    // Send message
    builder
      .addCase(sendMessage.pending, (state) => {
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        const message = action.payload;
        if (!state.messages[message.chatId]) {
          state.messages[message.chatId] = [];
        }
        
        // Remplacer le message optimiste par le message confirmé
        const optimisticIndex = state.messages[message.chatId].findIndex(
          m => m.metadata?.optimisticId === message.metadata?.optimisticId
        );
        
        if (optimisticIndex !== -1) {
          state.messages[message.chatId][optimisticIndex] = message;
        } else {
          state.messages[message.chatId].push(message);
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to send message';
      });
  },
});

// Actions
export const {
  selectChat,
  setWhatsAppFilters,
  clearWhatsAppFilters,
  toggleWhatsAppSidebar,
  setSidebarOpen,
  setChatWindowHeight,
  setTypingIndicator,
  setMessageInput,
  clearMessageInput,
  addOptimisticMessage,
  updateMessageStatus,
  receiveMessage,
  markMessagesAsRead,
  setWhatsAppError,
  clearWhatsAppError,
} = whatsappSlice.actions;

// Sélecteurs
export const selectSelectedChat = (state: { whatsapp: WhatsAppState }) =>
  state.whatsapp.chats.find(chat => chat.id === state.whatsapp.selectedChatId);

export const selectChatMessages = (chatId: string) => (state: { whatsapp: WhatsAppState }) =>
  state.whatsapp.messages[chatId] || [];

export const selectUnreadChatsCount = (state: { whatsapp: WhatsAppState }) =>
  state.whatsapp.chats.filter(chat => chat.unreadCount > 0).length;

export const selectFilteredChats = (state: { whatsapp: WhatsAppState }) => {
  const { chats, filters } = state.whatsapp;
  
  return chats.filter(chat => {
    if (filters.status && chat.status !== filters.status) return false;
    if (filters.agentId && chat.agentId !== filters.agentId) return false;
    if (filters.hasUnread && chat.unreadCount === 0) return false;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        chat.contactName?.toLowerCase().includes(searchLower) ||
        chat.phoneNumber.includes(searchLower) ||
        chat.lastMessage.content.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });
};

export default whatsappSlice.reducer;
